import React, { useEffect, useState, useMemo } from "react";
import "../../_css/Navbar.css";
import { deleteAuthToken } from "../../utils/helper";
import { useLocation, useNavigate } from "react-router-dom";
import Notification from "../../_pages/Notification";
import { useDispatch } from "react-redux";
import { show_notifications } from "../../_store/reducers/notificationReducer";
import { getUnreadNotifications } from "../../requests";
import { LockKeyhole } from "lucide-react";

const Navbar = () => {
  const location = useLocation();

  const [menu, setMenu] = useState(location.pathname);
  const [unreadNotifications, setUnreadNotifications] = useState(0);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [menuType, setMenuType] = useState(
    localStorage.getItem("ACCOUNT_STATUS") || 0
  );
  const [permissions, setPermissions] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("permissions")) || {};
    } catch {
      return {};
    }
  });

  // Function to update permissions from sessionStorage
  const updatePermissions = () => {
    try {
      const updatedPermissions = JSON.parse(sessionStorage.getItem("permissions")) || {};
      setPermissions((prev) => {
        // Only update if permissions actually changed
        const prevStr = JSON.stringify(prev);
        const newStr = JSON.stringify(updatedPermissions);
        if (prevStr !== newStr) {
          return updatedPermissions;
        }
        return prev;
      });
    } catch {
      setPermissions({});
    }
  };

  // Update permissions on mount and when location changes
  useEffect(() => {
    updatePermissions();
  }, [location]);

  // Also check permissions immediately on mount
  useEffect(() => {
    updatePermissions();
  }, []);

  // Listen for custom events and storage changes
  useEffect(() => {
    // Update when window gains focus (catches updates after navigation/refresh)
    const handleFocus = () => {
      updatePermissions();
    };

    // Listen for storage events (when sessionStorage is updated from other tabs/windows)
    const handleStorageChange = (e) => {
      if (e.key === "permissions") {
        updatePermissions();
      }
    };

    // Listen for custom permission update events (if dispatched elsewhere in the app)
    const handlePermissionUpdate = () => {
      updatePermissions();
    };

    window.addEventListener("focus", handleFocus);
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("permissionsUpdated", handlePermissionUpdate);

    return () => {
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("permissionsUpdated", handlePermissionUpdate);
    };
  }, []);

  // Memoize menus based on permissions to ensure they update when permissions change
  const teacherMenu = useMemo(() => ({
    Main: [
      {
        content: permissions?.uploadSheets ? "Upload Answer Sheets" : "Upload Sheets",
        disabled: !permissions?.uploadSheets,
        showLock: !permissions?.uploadSheets,
        fn: () => navigate("/"),
        url: "/",
      },
      {
        content: "AI Feedback",
        disabled: !permissions?.uploadSheets,
        showLock: !permissions?.uploadSheets,
        fn: () => navigate("/ai-feedback"),
        url: "/ai-feedback",
      },
    ],

    reportingSection: [
      {
        content: "Student Reports",
        disabled: !permissions?.studentReports,
        showLock: !permissions?.studentReports,
        url: "/student-reports",
        fn: () => navigate("/student-reports"),
      },
      {
        content: "Class summary",
        disabled: !permissions?.classSummary,
        showLock: !permissions?.classSummary,
        fn: () => navigate("/class"),
        url: "/class",
      },
      {
        content: "Report History",
        disabled: !permissions?.classSummary,
        showLock: !permissions?.classSummary,
        fn: () => navigate("/report-history"),
        url: "/report-history",
      },
    ],

    others: [
      {
        content: "Our Plans",
        fn: () => navigate("/our-plans"),
        url: "/our-plans",
        disabled: false
      },
      {
        content: "Add Ons",
        fn: () => navigate("/addons"),
        url: "/addons",
        disabled: false
      },
      {
        content: "Notification",
        fn: () => dispatch(show_notifications()),
        url: "/notify",
        hasBadge: true,
      },
      // {
      //   content: "Static Content",
      //   fn: () => navigate("/static-content/terms-and-conditions"),
      //   url: "/static-content",
      // },
      {
        content: "Help",
        fn: () => navigate("/help"),
        url: "/help",
      },
   
      {
        content: "Settings",
        disabled: false,
        fn: () => navigate("/settings"),
        url: "/settings",
      },
      {
        content: "Terms and Conditions",
        fn: () => navigate("/terms-and-conditions"),
        url: "/terms-and-conditions",
      },
      {
        content: "Logout",
        fn: () => (deleteAuthToken(), navigate("/login")),
      },
    ],
  }), [permissions, navigate, dispatch]);
  
  const adminMenu = useMemo(() => ({
    Main: [
      {
        content: "Overview",
        disabled: false,
        fn: () => navigate("/"),
        url: "/",
      },
      {
        content: "Teacher Management",
        fn: () => navigate("/teacher-management"),
        url: "/teacher-management",
      },
      {
        content: "Assignment Monitoring",
        disabled: false,
        fn: () => navigate("/assignments"),
        url: "/assignments",
      },
    ],

    reportingSection: [
      // { content: "Usage Reports", disabled: true },
      {
        content: "Reports Export",
        disabled: false,
        fn: () => navigate("/report-export"),
        url: "/report-export",
      },
      {
        content: "Review & Accuracy Tracking",
        disabled: false,
        fn: () => navigate("/review-accuracy-tracking"),
        url: "/review-accuracy-tracking",
      },
      {
        content: "School Management",
        disabled: false,
        fn: () => navigate("/school-management"),
        url: "/school-management",
      },
    ],

    payments: [
      {
        content: "Plan Management",
        disabled: false,
        fn: () => navigate("/plan-management"),
        url: "/plan-management",
      },
      {
        content: "Add On Management",
        disabled: false,
        fn: () => navigate("/manage-add-on"),
        url: "/manage-add-on",
      },
      {
        content: "Subscriptions",
        disabled: false,
        fn: () => navigate("/subscription"),
        url: "/subscription",
      },

    ],

    others: [
      {
        content: "Notification Management",
        disabled: false,
        fn: () => navigate("/notification-mng"),
        url: "/notification-mng",
      },
      // {
      //   content: "CMS",
      //   disabled: false,
      //   fn: () => navigate("/cms"),
      //   url: "/cms",
      // },

      {
        content: "Logout",
        fn: () => (deleteAuthToken(), navigate("/login")),
      },
    ],
  }), [navigate, dispatch]);

  const getAllUnreadNotifications = async () => {
    try {
      const response = await getUnreadNotifications();
      if (response.status < 400) {
        setUnreadNotifications(response.data.count);
      }
      
    } catch (error) {
      console.error("Error fetching unread notifications:", error);
    }
  };

  const Mymenu = menuType == 0 ? teacherMenu : adminMenu;

  useEffect(() => {
    const pathSegments = location.pathname.split("/");
    // console.log(pathSegments);
    let firstPath = `/${pathSegments[1]}` || location.pathname;
    // console.log(firstPath);

    if (firstPath == "/student-list" || firstPath == "/overview") {
      firstPath = "/";
    }

    setMenu(firstPath);
  }, [location]);

  useEffect(() => {
    getAllUnreadNotifications();
  }, [location]);

  return (
    <div className="navbar-container">
      <div className="navbar-logo pe-cursor" onClick={() => navigate("/")}>
        <img src="/AI-Marking.png" width="90%" height="125%" />
        {/* <div className="navbar-logo-1">
          <img src="/img/aiLogo.png" width="100%" height="100%" />
        </div>
        <div className="navbar-logo-2">
          <img src="/img/aiLogo2.png" width="100%" height="100%" />
        </div> */}
      </div>

      <div className="navbar-menu">
        <div className="navbar-top-menu">
          <div className="navbar-top-main">
            <div className="navbar-menu-heading">Main</div>
            {Mymenu.Main.map((e, i) => (
              <div
                style={{ opacity: e.disabled && "0.5" }}
                key={i}
                onClick={() => {
                  if (!e.disabled) {
                    setMenu(e?.url);
                    e?.fn && e.fn();
                  }
                }}
                className={
                  menu == e?.url
                    ? "navbar-menu-item-selected"
                    : "navbar-menu-item"
                }
              >
                <img
                  className="navbar-menu-icon"
                  src={
                    menu == e?.url ? "/3d-select-point.svg" : "/menu-icon.svg"
                  }
                />
                <div
                  className={
                    menu == e?.url
                      ? "navbar-item-text-selected"
                      : "navbar-item-text"
                  }
                  style={{ display: "flex", alignItems: "center", gap: "4px" }}
                >
                  {e?.content}
                  {e?.showLock && <LockKeyhole size={16} />}
                </div>
              </div>
            ))}
          </div>

          <div className="navbar-top-section">
            <div className="navbar-menu-heading">Reporting Section</div>
            {Mymenu.reportingSection.map((e, i) => (
              <div
                style={{ opacity: e.disabled && "0.5" }}
                key={i}
                onClick={() => {
                  if (!e.disabled) {
                    setMenu(e?.url);
                    e?.fn && e.fn();
                  }
                }}
                className={
                  menu == e?.url
                    ? "navbar-menu-item-selected"
                    : "navbar-menu-item"
                }
              >
                <img
                  className="navbar-menu-icon"
                  src={
                    menu == e?.url ? "/3d-select-point.svg" : "/menu-icon.svg"
                  }
                />
                <div
                  className={
                    menu == e?.url
                      ? "navbar-item-text-selected"
                      : "navbar-item-text"
                  }
                  style={{ display: "flex", alignItems: "center", gap: "4px" }}
                >
                  {e?.content}
                  {e?.showLock && <LockKeyhole size={16} />}
                </div>
              </div>
            ))}
          </div>

          {menuType != 0 && Mymenu.payments && Mymenu.payments.length > 0 && (
            <div className="navbar-top-section">
              <div className="navbar-menu-heading">Payments</div>
              {Mymenu.payments.map((e, i) => (
                <div
                  style={{ opacity: e.disabled && "0.5" }}
                  key={i}
                  onClick={() => {
                    if (!e.disabled) {
                      setMenu(e?.url);
                      e?.fn && e.fn();
                    }
                  }}
                  className={
                    menu == e?.url
                      ? "navbar-menu-item-selected"
                      : "navbar-menu-item"
                  }
                >
                  <img
                    className="navbar-menu-icon"
                    src={
                      menu == e?.url ? "/3d-select-point.svg" : "/menu-icon.svg"
                    }
                  />
                  <div
                    className={
                      menu == e?.url
                        ? "navbar-item-text-selected"
                        : "navbar-item-text"
                    }
                    style={{ display: "flex", alignItems: "center", gap: "4px" }}
                  >
                    {e?.content}
                    {e?.showLock && <LockKeyhole size={16} />}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="navbar-bottom-menu">
          <div className="navbar-menu-heading">Others</div>
          {Mymenu.others.map((e, i) => (
            <div
              style={{ opacity: e.disabled && "0.5" }}
              key={i}
              onClick={() => {
                if (!e.disabled) {
                  setMenu(e?.url);
                  e?.fn && e.fn();
                  if (e?.hasBadge) {
                    setUnreadNotifications(0);
                  }
                }
              }}
              className={
                menu == e?.url
                  ? "navbar-menu-item-selected"
                  : "navbar-menu-item"
              }
            >
              <img
                className="navbar-menu-icon"
                src={menu == e?.url ? "/3d-select-point.svg" : "/menu-icon.svg"}
              />
              <div
                className={
                  menu == e?.url
                    ? "navbar-item-text-selected"
                    : "navbar-item-text"
                }
              >
                {e?.content}
              </div>
              {e?.hasBadge && unreadNotifications > 0 && (
                <div className="notification-badge">
                  {unreadNotifications > 99 ? "99+" : unreadNotifications}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Navbar;
